// src/components/Canvas/IconPlacementPreview/IconPlacementPreview.tsx
/**
 * IconPlacementPreview - Shows the icon being dragged on the canvas
 *
 * This component displays a semi-transparent preview of the icon
 * that follows the mouse cursor during placement.
 * 
 * ✨ UPDATED: Hides when hovering over an existing icon for replacement
 */
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useUiStateStore } from '@/stores/uiStateStore';
import { useScene } from '@/hooks/useScene';
import { useIcon } from '@/hooks/useIcon';
import { getTilePosition, getItemAtTile } from '@/utils';

export const IconPlacementPreview: React.FC = () => {
  const mode = useUiStateStore((state) => state.mode);
  const mouse = useUiStateStore((state) => state.mouse);
  const scene = useScene();

  // Only show preview when in PLACE_ICON mode with an icon selected
  const shouldShowPreview = mode.type === 'PLACE_ICON' && mode.id !== null;

  // ✨ NEW: Check if hovering over an existing item for replacement
  const isHoveringForReplacement = useMemo(() => {
    if (!shouldShowPreview) return false;

    const itemAtTile = getItemAtTile({
      tile: mouse.position.tile,
      scene
    });

    return itemAtTile?.type === 'ITEM';
  }, [shouldShowPreview, mouse.position.tile, scene]);

  // Get the icon to preview
  const { iconComponent } = useIcon(
    shouldShowPreview ? mode.id! : '',
    shouldShowPreview ? `preview-${mode.id}` : ''
  );

  // Calculate the position for the preview
  const previewPosition = useMemo(() => {
    if (!shouldShowPreview) return null;

    return getTilePosition({
      tile: mouse.position.tile,
      origin: 'BOTTOM'
    });
  }, [shouldShowPreview, mouse.position.tile]);

  // ✨ NEW: Don't render if hovering over an item (IconReplacementHover will show it)
  if (!shouldShowPreview || !previewPosition || isHoveringForReplacement) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        opacity: 0.7, // Semi-transparent to show it's a preview
        pointerEvents: 'none', // Don't interfere with mouse events
        zIndex: 999 // Render above other elements
      }}
      style={{
        left: previewPosition.x,
        top: previewPosition.y
      }}
    >
      {iconComponent}
    </Box>
  );
};